import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { useConvertClientToPlanner, usePlannerConversionPreview } from '@/hooks/useEvents'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { FormError } from '@/components/ui/form-error'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatEventDate } from '@/lib/eventTime'
import type { EventClientDto, EventDetailDto } from '@lib/dto/event.dto'
import type { ClientToPlannerDto } from '@lib/dto/planner-conversion.dto'
import { clientToPlannerInputSchema } from '@lib/input/planner-conversion.input'

/** What it will do: the events here, how many elsewhere, who is emailed, and what stops it. */
function PreviewBody({ preview }: { preview: ClientToPlannerDto }): React.ReactElement {
  const count = preview.events.length
  return (
    <div className="space-y-3">
      <p>
        {preview.personName} becomes the planner, not a client, on{' '}
        {count === 1 ? 'this event' : `these ${String(count)} events`} here, as the owner of {preview.companyName}:
      </p>
      <ul className="max-h-40 list-disc space-y-0.5 overflow-y-auto pl-5">
        {preview.events.map((e) => (
          <li key={e.eventId}>
            <span className="font-medium text-ink">{e.eventName}</span>
            {' — '}
            {e.eventDate ? formatEventDate(e.eventDate) : 'no date yet'}, {e.venueName}
          </li>
        ))}
      </ul>
      {preview.otherEvents > 0 ? (
        <p>
          And on {preview.otherEvents === 1 ? '1 more event' : `${String(preview.otherEvents)} more events`} at venues
          you do not manage, where they are a client too.
        </p>
      ) : null}
      {/* What happens to them next only says so when it can happen: beside a
          problem, "they sign in with the same password" would be untrue. */}
      {preview.problems.length > 0 ? (
        <ul className="list-disc space-y-0.5 pl-5 text-danger-ink">
          {preview.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : (
        <p>
          {preview.invites
            ? 'They have not signed in yet, so they get one invitation, naming this event.'
            : 'They sign in with the same email and password, and are not emailed.'}{' '}
          Nobody else is told.
        </p>
      )}
    </div>
  )
}

/**
 * Mark one of the event's clients as a planner (docs/planner-vendors.md,
 * decision 32): for a planner an import brought in as a client. They become the
 * planner on every event they are a client on, at any venue, with a company of
 * their own, named for them unless it is named here. The warning says what
 * happens before anything does, and nothing can be confirmed while it lists a
 * problem, while it is being checked, or for a name other than the one it
 * checked.
 *
 * An invitation that could not be sent is handed to `onInvitationUnsent` rather
 * than said here: once they are the planner they leave the client list, and the
 * row this dialog belongs to goes with them.
 */
export function ClientToPlannerDialog({
  event,
  client,
  open,
  onOpenChange,
  onInvitationUnsent,
}: {
  event: EventDetailDto
  client: EventClientDto
  open: boolean
  onOpenChange: (open: boolean) => void
  /** They are the planner, and their invitation is what is left to send. */
  onInvitationUnsent: () => void
}): React.ReactElement {
  const [companyName, setCompanyName] = React.useState(client.name)
  // The shared rule, so a name the API would refuse is said on the field instead of replacing the warning.
  // Left empty, the company is named for them, as the placeholder shows.
  const parsed = clientToPlannerInputSchema.safeParse(companyName.trim() === '' ? {} : { companyName })
  const fieldError = parsed.success ? null : (parsed.error.issues[0]?.message ?? 'Enter the company’s name.')
  const asked = useDebouncedValue(companyName.trim(), 400)
  const preview = usePlannerConversionPreview(event.id, client.id, asked, open && fieldError === null)
  const convert = useConvertClientToPlanner(event.id)
  const shown = preview.data

  function body(): React.ReactNode {
    if (fieldError !== null) return null
    if (preview.isPending) {
      return (
        <span className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking their events…
        </span>
      )
    }
    if (preview.isError) return requestErrorMessage(preview.error)
    return shown ? <PreviewBody preview={shown} /> : null
  }

  const ready =
    fieldError === null &&
    asked === companyName.trim() &&
    preview.isSuccess &&
    !preview.isFetching &&
    !preview.isPlaceholderData &&
    shown !== undefined &&
    shown.problems.length === 0

  // Not closed while the conversion is under way: closing would not stop it, and its answer would be lost.
  const close = (next: boolean): void => {
    if (!next && convert.isPending) return
    if (!next) convert.reset()
    onOpenChange(next)
  }

  function confirm(): void {
    const name = companyName.trim()
    convert.mutate(
      { clientId: client.id, input: name === '' ? {} : { companyName: name } },
      {
        onSuccess: (done) => {
          if (done.invitationSent === false) onInvitationUnsent()
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Make {client.name} a planner?</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-4">
              <Field label="Their company" htmlFor={`planner-company-${client.id}`} error={fieldError ?? undefined}>
                <Input
                  id={`planner-company-${client.id}`}
                  value={companyName}
                  disabled={convert.isPending}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={client.name}
                />
              </Field>
              {body()}
            </div>
          </DialogDescription>
        </DialogHeader>
        {convert.error ? <FormError>{requestErrorMessage(convert.error)}</FormError> : null}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={convert.isPending} onClick={() => close(false)}>
            Cancel
          </Button>
          <Button pending={convert.isPending} type="button" disabled={!ready} onClick={confirm}>
            Make them the planner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
